import {groupBy, pick} from '@benchling/js-utils';
import produce from 'immer';

import {pluralize} from 'BenchlingUtil';
import {
  DEFAULT_MEASUREMENT,
  DEFAULT_MEASUREMENT_VALIDATION_RESULT,
  Measurement,
  MeasurementValidationResult,
} from 'constants/MeasurementConstants';
import {
  BioentityContent,
  PlateMap,
  PlateMapSize,
  PlateMaterialInput,
  PlateMapWell,
  ReagentType,
  Source,
} from 'samples/plate-maps/PlateMapConstants';
import {getFlattenedWellIndex} from 'samples/plate-maps/PlateMapUtil';
import {
  BASE_WRONG_SCHEMA_MATERIAL_INPUT_ERROR_MESSAGE,
  DEFAULT_MEASUREMENT_VALIDATION_ERRORS,
  MeasurementValidationErrors,
  MeasurementWithErrors,
  ReagentValueValidationError,
  SUM_OF_TRANSFER_QUANTITIES_EXCEEDS_TOTAL_QUANTITY_ERROR_MESSAGE,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationConstants';
import {
  FinalConcentrationPlateMapError,
  FinalQuantityPlateMapError,
  MaterialInputPlateMapError,
  MeasurementComponent,
  PlateMapErrorLevel,
  PlateMapReagentValueType,
  PlateMapValidationResponse,
  ReagentValuePlateMapError,
  SourceConcentrationPlateMapError,
  TransferQuantityPlateMapError,
} from 'samples/plate-maps/validation/ValidationTypes';
import {isRequiredLinkTagSchema} from 'schemas/fields/SchemaFieldConstants';

export function getSumOfTransferQuantitiesExceedsFinalQuantityErrorMessage(numWells: number): string {
  return `${SUM_OF_TRANSFER_QUANTITIES_EXCEEDS_TOTAL_QUANTITY_ERROR_MESSAGE} for ${numWells} ${pluralize(
    numWells,
    'well'
  )}`;
}

export const getWrongSchemaMaterialInputErrorMessage = (schemaName: string): string =>
  `${BASE_WRONG_SCHEMA_MATERIAL_INPUT_ERROR_MESSAGE} ${schemaName}`;

export function getDefaultMeasurement(): Measurement {
  return {...DEFAULT_MEASUREMENT};
}

export function getDefaultMeasurementValidationResult(): MeasurementValidationResult {
  return {...DEFAULT_MEASUREMENT_VALIDATION_RESULT};
}

export function getDefaultMeasurementValidationErrors(): MeasurementValidationErrors {
  return {...DEFAULT_MEASUREMENT_VALIDATION_ERRORS};
}

export function getDefaultMeasurementWithErrors(): MeasurementWithErrors<Measurement> {
  return {
    measurement: getDefaultMeasurement(),
    errors: getDefaultMeasurementValidationErrors(),
  };
}

function isBlockingError(maybeError: ReagentValueValidationError | null): boolean {
  return maybeError != null && maybeError.level === PlateMapErrorLevel.ERROR;
}

/**
 * Determines whether either field in a MeasurementValidationResult is non-null, i.e. representing an error
 */
export function hasErrors(measurementValidationErrors: MeasurementValidationErrors): boolean {
  return (
    isBlockingError(measurementValidationErrors.valueError) ||
    isBlockingError(measurementValidationErrors.unitsError)
  );
}

export function isValidSchemaForMaterialInput(materialInput: PlateMaterialInput, schemaId: string): boolean {
  const {requiredLink} = materialInput.schemaField;
  if (
    requiredLink != null &&
    isRequiredLinkTagSchema(requiredLink) &&
    requiredLink.tagSchema.id !== schemaId
  ) {
    return false;
  }
  return true;
}

/**
 * Determines whether any attributes on a well, its sources, or its contents have any errors.
 * @param well The well to validate
 */
export function wellHasAnyErrors(well: PlateMapWell): boolean {
  if (hasErrors(well.finalQuantity.errors)) {
    return true;
  }
  if (
    well.contents.some(
      (content) => hasErrors(content.finalConcentration.errors) || content.materialInput?.error
    )
  ) {
    return true;
  }
  return well.sources.some((source) => {
    if (hasErrors(source.transferQuantity.errors)) {
      return true;
    }
    if (source.type === ReagentType.SOURCE_CONTAINABLE) {
      return (
        hasErrors(source.finalConcentration.errors) ||
        hasErrors(source.sourceConcentration.errors) ||
        source.materialInput?.error
      );
    } else {
      return source.contents.some(
        (content) =>
          hasErrors(content.finalConcentration.errors) ||
          hasErrors(content.sourceConcentration.errors) ||
          content.materialInput?.error
      );
    }
  });
}

/**
 * Determines whether any measurements on any wells, sources, or contents in the plate map have any errors.
 * @param plateMap The plate map to validate
 */
export function plateMapHasAnyErrors(plateMap: PlateMap): boolean {
  return plateMap.wells.some((well) => wellHasAnyErrors(well));
}

/**
 * Categorizes the reagent value errors in a plate map validation response by reagent value type.
 */
function groupErrorsByType(validation: PlateMapValidationResponse): {
  sourceConcentrationErrors: Array<SourceConcentrationPlateMapError>;
  transferQuantityErrors: Array<TransferQuantityPlateMapError>;
  finalConcentrationErrors: Array<FinalConcentrationPlateMapError>;
  finalQuantityErrors: Array<FinalQuantityPlateMapError>;
  materialInputErrors: Array<MaterialInputPlateMapError>;
} {
  const sourceConcentrationErrors: Array<SourceConcentrationPlateMapError> = [];
  const transferQuantityErrors: Array<TransferQuantityPlateMapError> = [];
  const finalConcentrationErrors: Array<FinalConcentrationPlateMapError> = [];
  const finalQuantityErrors: Array<FinalQuantityPlateMapError> = [];
  const materialInputErrors: Array<MaterialInputPlateMapError> = [];
  for (const error of validation.reagentValueValidation) {
    switch (error.reagentValueType) {
      case PlateMapReagentValueType.SOURCE_CONCENTRATION:
        sourceConcentrationErrors.push(error);
        break;
      case PlateMapReagentValueType.TRANSFER_QUANTITY:
        transferQuantityErrors.push(error);
        break;
      case PlateMapReagentValueType.FINAL_CONCENTRATION:
        finalConcentrationErrors.push(error);
        break;
      case PlateMapReagentValueType.FINAL_QUANTITY:
        finalQuantityErrors.push(error);
        break;
      case PlateMapReagentValueType.MATERIAL_INPUT:
        materialInputErrors.push(error);
        break;
      default:
        break;
    }
  }
  return {
    sourceConcentrationErrors,
    transferQuantityErrors,
    finalConcentrationErrors,
    finalQuantityErrors,
    materialInputErrors,
  };
}

/**
 * Given a reagent and the index of the well where it is located, create a unique identifier that can be used
 * to correlate that reagent with pertinent validation errors.
 * @param containerId Optionally, the API ID of the container in which the reagent is contained
 * @param reagent The reagent to get an identifier for
 * @param wellIndex The index of the well where the reagent is located or will be transferred to
 */
export function getValidationReagentIdForReagent({
  containerId,
  reagent,
  wellIndex,
}: {
  containerId?: string;
  reagent: Source | BioentityContent;
  wellIndex: number;
}): string {
  return `${reagent.type === ReagentType.WELL_CONTENT ? 'CONTENT' : 'SOURCE'}-${wellIndex}-${reagent.id}${containerId != null ? `-${containerId}` : ''}`;
}

export function getValidationReagentIdForError(
  // Final quantity errors only ever pertain to a well
  error: Exclude<ReagentValuePlateMapError, FinalQuantityPlateMapError>,
  plateMapSize: PlateMapSize
): string {
  const wellIndex = getFlattenedWellIndex(error.coordinates.row, error.coordinates.column, plateMapSize);
  const reagentId =
    error.reagentValueType === PlateMapReagentValueType.TRANSFER_QUANTITY
      ? error.reagentId
      : error.containableId;
  const containerId =
    error.reagentValueType === PlateMapReagentValueType.FINAL_CONCENTRATION ||
    error.reagentValueType === PlateMapReagentValueType.MATERIAL_INPUT
      ? error.containerId
      : null;
  return `${error.reagentType}-${wellIndex}-${reagentId}${containerId != null ? `-${containerId}` : ''}`;
}

function pickTableErrorFields(responseError: ReagentValuePlateMapError): ReagentValueValidationError {
  return pick(responseError, 'message', 'level');
}

export function hydrateReagentErrorsFromValidation(
  plateMap: PlateMap,
  validation: PlateMapValidationResponse
): PlateMap {
  const {
    sourceConcentrationErrors,
    transferQuantityErrors,
    finalConcentrationErrors,
    finalQuantityErrors,
    materialInputErrors,
  } = groupErrorsByType(validation);
  const sourceConcentrationErrorsByErrorId = groupBy(
    sourceConcentrationErrors,
    (error: SourceConcentrationPlateMapError) => getValidationReagentIdForError(error, plateMap.size)
  );
  const transferQuantityErrorsByErrorId = groupBy(
    transferQuantityErrors,
    (error: TransferQuantityPlateMapError) => getValidationReagentIdForError(error, plateMap.size)
  );
  const finalConcentrationErrorsByErrorId = groupBy(
    finalConcentrationErrors,
    (error: FinalConcentrationPlateMapError) => getValidationReagentIdForError(error, plateMap.size)
  );
  const finalQuantityErrorsByWellIndex = groupBy(finalQuantityErrors, (error) =>
    getFlattenedWellIndex(error.coordinates.row, error.coordinates.column, plateMap.size)
  );
  const materialInputErrorsByErrorId = groupBy(materialInputErrors, (error: MaterialInputPlateMapError) =>
    getValidationReagentIdForError(error, plateMap.size)
  );

  return produce(plateMap, (draft) => {
    for (const well of draft.wells) {
      const wellIndex = getFlattenedWellIndex(well.position.row, well.position.column, plateMap.size);
      const finalQuantityErrors = finalQuantityErrorsByWellIndex[wellIndex] ?? [];
      for (const finalQuantityError of finalQuantityErrors) {
        if (finalQuantityError.measurementComponent === MeasurementComponent.VALUE) {
          well.finalQuantity.errors.valueError = pickTableErrorFields(finalQuantityError);
        } else {
          well.finalQuantity.errors.unitsError = pickTableErrorFields(finalQuantityError);
        }
      }
      for (const source of well.sources) {
        const sourceErrorId = getValidationReagentIdForReagent({reagent: source, wellIndex});
        const transferQuantityErrors = transferQuantityErrorsByErrorId[sourceErrorId] ?? [];
        for (const transferQuantityError of transferQuantityErrors) {
          if (transferQuantityError.measurementComponent === MeasurementComponent.VALUE) {
            source.transferQuantity.errors.valueError = pickTableErrorFields(transferQuantityError);
          } else {
            source.transferQuantity.errors.unitsError = pickTableErrorFields(transferQuantityError);
          }
        }
        if (source.type === ReagentType.SOURCE_CONTAINABLE) {
          const sourceConcentrationErrors = sourceConcentrationErrorsByErrorId[sourceErrorId] ?? [];
          for (const sourceConcentrationError of sourceConcentrationErrors) {
            if (sourceConcentrationError.measurementComponent === MeasurementComponent.VALUE) {
              source.sourceConcentration.errors.valueError = pickTableErrorFields(sourceConcentrationError);
            } else {
              source.sourceConcentration.errors.unitsError = pickTableErrorFields(sourceConcentrationError);
            }
          }
          const finalConcentrationErrors = finalConcentrationErrorsByErrorId[sourceErrorId] ?? [];
          for (const finalConcentrationError of finalConcentrationErrors) {
            if (finalConcentrationError.measurementComponent === MeasurementComponent.VALUE) {
              source.finalConcentration.errors.valueError = pickTableErrorFields(finalConcentrationError);
            } else {
              source.finalConcentration.errors.unitsError = pickTableErrorFields(finalConcentrationError);
            }
          }
          const materialInputErrors = materialInputErrorsByErrorId[sourceErrorId] ?? [];
          for (const materialInputError of materialInputErrors) {
            source.materialInput!.error = pickTableErrorFields(materialInputError);
          }
        } else {
          for (const content of source.contents) {
            const contentErrorId = getValidationReagentIdForReagent({
              containerId: source.id,
              reagent: content,
              wellIndex,
            });
            const finalConcentrationErrors = finalConcentrationErrorsByErrorId[contentErrorId] ?? [];
            for (const finalConcentrationError of finalConcentrationErrors) {
              if (finalConcentrationError.measurementComponent === MeasurementComponent.VALUE) {
                content.finalConcentration.errors.valueError = pickTableErrorFields(finalConcentrationError);
              } else {
                content.finalConcentration.errors.unitsError = pickTableErrorFields(finalConcentrationError);
              }
            }
            const materialInputErrors = materialInputErrorsByErrorId[contentErrorId] ?? [];
            for (const materialInputError of materialInputErrors) {
              content.materialInput!.error = pickTableErrorFields(materialInputError);
            }
          }
        }
      }
      for (const wellContent of well.contents) {
        const wellContentErrorId = getValidationReagentIdForReagent({reagent: wellContent, wellIndex});
        const finalConcentrationErrors = finalConcentrationErrorsByErrorId[wellContentErrorId] ?? [];
        for (const finalConcentrationError of finalConcentrationErrors) {
          if (finalConcentrationError.measurementComponent === MeasurementComponent.VALUE) {
            wellContent.finalConcentration.errors.valueError = pickTableErrorFields(finalConcentrationError);
          } else {
            wellContent.finalConcentration.errors.unitsError = pickTableErrorFields(finalConcentrationError);
          }
        }
        const materialInputErrors = materialInputErrorsByErrorId[wellContentErrorId] ?? [];
        for (const materialInputError of materialInputErrors) {
          wellContent.materialInput!.error = pickTableErrorFields(materialInputError);
        }
      }
    }
  });
}
